/**
 * Industry Pack resource type seed editor (DR2). Each type defines scheduling
 * ontology attributes that downstream sched_* tables may consume.
 */

import React from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import type { ResourceTypeSeed } from '@/types/aiStudioAgent';
import { FIELD_CLASS, LABEL_CLASS } from './constants';

interface ResourceTypesEditorProps {
  types: ResourceTypeSeed[];
  disabled?: boolean;
  onChange: (types: ResourceTypeSeed[]) => void;
}

const emptyType = (): ResourceTypeSeed => ({
  typeCode: '',
  displayName: '',
  attributes: [],
});

export const ResourceTypesEditor: React.FC<ResourceTypesEditorProps> = ({
  types,
  disabled = false,
  onChange,
}) => {
  const { t } = useLanguage();

  const updateType = (index: number, patch: Partial<ResourceTypeSeed>) => {
    onChange(types.map((type, i) => (i === index ? { ...type, ...patch } : type)));
  };

  const addAttribute = (typeIndex: number) => {
    const type = types[typeIndex];
    updateType(typeIndex, {
      attributes: [
        ...type.attributes,
        { attributeCode: '', dataType: 'string', isRequired: false },
      ],
    });
  };

  const updateAttribute = (
    typeIndex: number,
    attrIndex: number,
    patch: Partial<ResourceTypeSeed['attributes'][number]>
  ) => {
    const type = types[typeIndex];
    updateType(typeIndex, {
      attributes: type.attributes.map((attr, i) =>
        i === attrIndex ? { ...attr, ...patch } : attr
      ),
    });
  };

  const removeAttribute = (typeIndex: number, attrIndex: number) => {
    const type = types[typeIndex];
    updateType(typeIndex, {
      attributes: type.attributes.filter((_, i) => i !== attrIndex),
    });
  };

  return (
    <fieldset>
      <legend className={LABEL_CLASS}>{t('aiStudio.definition.resourceTypes')}</legend>
      <p className="mb-2 text-base text-[var(--color-surface-text-secondary)]">
        {t('aiStudio.definition.resourceTypesHint')}
      </p>

      <div className="flex flex-col gap-3">
        {types.map((type, typeIndex) => (
          <div
            key={typeIndex}
            className="rounded border border-[var(--color-border)] bg-[var(--color-surface-bg)] p-3"
          >
            <div className="mb-2 grid gap-2 md:grid-cols-2 lg:grid-cols-4">
              <div>
                <label className={LABEL_CLASS}>{t('aiStudio.definition.typeCode')}</label>
                <input
                  className={FIELD_CLASS}
                  value={type.typeCode}
                  disabled={disabled}
                  onChange={(e) => updateType(typeIndex, { typeCode: e.target.value })}
                />
              </div>
              <div>
                <label className={LABEL_CLASS}>{t('aiStudio.definition.displayName')}</label>
                <input
                  className={FIELD_CLASS}
                  value={type.displayName}
                  disabled={disabled}
                  onChange={(e) => updateType(typeIndex, { displayName: e.target.value })}
                />
              </div>
              {!disabled && (
                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={() => onChange(types.filter((_, i) => i !== typeIndex))}
                    className="text-base text-[var(--color-error)] hover:underline"
                  >
                    {t('aiStudio.common.remove')}
                  </button>
                </div>
              )}
            </div>

            <p className="mb-1 text-base font-medium text-[var(--color-surface-text)]">
              {t('aiStudio.definition.attributes')}
            </p>
            {type.attributes.map((attr, attrIndex) => (
              <div
                key={attrIndex}
                className="mb-2 grid gap-2 md:grid-cols-2 lg:grid-cols-4"
              >
                <div>
                  <label className={LABEL_CLASS}>{t('aiStudio.definition.attributeCode')}</label>
                  <input
                    className={FIELD_CLASS}
                    value={attr.attributeCode}
                    disabled={disabled}
                    onChange={(e) =>
                      updateAttribute(typeIndex, attrIndex, { attributeCode: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className={LABEL_CLASS}>{t('aiStudio.definition.dataType')}</label>
                  <input
                    className={FIELD_CLASS}
                    value={attr.dataType}
                    disabled={disabled}
                    onChange={(e) =>
                      updateAttribute(typeIndex, attrIndex, { dataType: e.target.value })
                    }
                  />
                </div>
                <div className="flex items-end gap-2">
                  <label className="flex items-center gap-2 text-base text-[var(--color-surface-text)]">
                    <input
                      type="checkbox"
                      checked={attr.isRequired ?? false}
                      disabled={disabled}
                      onChange={(e) =>
                        updateAttribute(typeIndex, attrIndex, { isRequired: e.target.checked })
                      }
                    />
                    {t('aiStudio.definition.rowRequired')}
                  </label>
                  {!disabled && (
                    <button
                      type="button"
                      onClick={() => removeAttribute(typeIndex, attrIndex)}
                      className="text-base text-[var(--color-error)] hover:underline"
                    >
                      {t('aiStudio.common.remove')}
                    </button>
                  )}
                </div>
              </div>
            ))}

            {!disabled && (
              <button
                type="button"
                onClick={() => addAttribute(typeIndex)}
                className="text-base text-[var(--color-info)] hover:underline"
              >
                {t('aiStudio.definition.addAttribute')}
              </button>
            )}
          </div>
        ))}
      </div>

      {!disabled && (
        <button
          type="button"
          onClick={() => onChange([...types, emptyType()])}
          className="mt-3 rounded-md border border-[var(--color-border)] px-3 py-1 text-base text-[var(--color-surface-text)] hover:bg-[var(--color-surface-hover)]"
        >
          {t('aiStudio.definition.addResourceType')}
        </button>
      )}
    </fieldset>
  );
};
